import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { loadSection } from "../services/contentSource";
import positionsFallback from "../data/careerPositions.json";
import Seo from "../components/Seo";

const CareerDetails = () => {
  const { id } = useParams();
  const [position, setPosition] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Application form state
  const [applicantName, setApplicantName] = useState("");
  const [applicantEmail, setApplicantEmail] = useState("");
  const [applicantPhone, setApplicantPhone] = useState("");
  const [applicantPortfolio, setApplicantPortfolio] = useState("");
  const [applicantNote, setApplicantNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadSection("careerPositions", positionsFallback as any)
      .then(({ data: main }) => {
        const found = ((main as any[]) || []).find((job: any) => job.id === id);
        setPosition(found || null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleScrollToApply = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.getElementById("apply-section");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleApplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicantName || !applicantEmail) return;

    setSubmitting(true);
    // Simulate swift submission & save candidate state
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 600);
  };

  if (loading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ fontSize: "18px", color: "var(--secondary, #8E8E8E)" }}>Loading position details...</p>
      </div>
    );
  }

  if (!position) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px" }}>
        <h2 style={{ fontSize: "28px" }}>Position Not Found</h2>
        <p style={{ color: "var(--secondary, #8E8E8E)" }}>The role you are looking for may have closed or moved.</p>
        <Link to="/career" className="wc-btn wc-btn-primary" style={{ padding: "12px 28px", borderRadius: "8px", textDecoration: "none" }}>
          Browse All Open Positions
        </Link>
      </div>
    );
  }

  const mailtoUrl = `mailto:careers@mindsyncx.com?subject=${encodeURIComponent(
    `Application: ${position.title} - ${applicantName || "Candidate"}`
  )}&body=${encodeURIComponent(
    `Hi MindSyncX Talent Team,\n\nI am applying for the ${position.title} role.\n\nName: ${applicantName}\nEmail: ${applicantEmail}\nPhone: ${applicantPhone}\nPortfolio / LinkedIn: ${applicantPortfolio}\n\nNote:\n${applicantNote}\n\nLooking forward to hearing from you!\n`
  )}`;

  return (
    <>
      <Seo
        title={`${position.title} | Careers at MindSyncX`}
        description={position.description}
        path={`/career-details/${position.id}`}
      />
      <link
        rel="stylesheet"
        href="/assets/css/master-career-details.css"
      />
      <main>
        <section className="career-details-area">
          {/* Breadcrumb Navigation */}
          <div className="container" style={{ marginBottom: "24px" }}>
            <Link
              to="/career"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                color: "var(--secondary, #8E8E8E)",
                fontSize: "15px",
                fontWeight: 500,
                textDecoration: "none",
                transition: "color 0.2s ease"
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--primary, #121212)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--secondary, #8E8E8E)")}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back to all open roles
            </Link>
          </div>

          {/* about area start */}
          <div className="about-area">
            <div className="area-bg">
              <img
                src={position.heroImage}
                className="has_fade_anim"
                data-fade-offset={0}
                alt={`${position.title} hero banner`}
                loading="lazy"
                decoding="async"
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(to right, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 100%)",
                  pointerEvents: "none"
                }}
              />
            </div>
            <div className="container">
              <div className="about-area-inner">
                <div className="section-content has_fade_anim" style={{ boxShadow: "0 20px 40px rgba(0,0,0,0.12)", borderRadius: "8px", overflow: "hidden" }}>
                  <div className="content-first">
                    <ul className="job-meta">
                      {(position.meta || []).map((item: any, idx: number) => (
                        <li key={idx}>
                          <span className="title">{item.title}</span>
                          <span className="text">{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="btn-wrapper">
                    <a
                      href="#apply-section"
                      onClick={handleScrollToApply}
                      className="wc-btn wc-btn-normal btn-text-flip"
                      style={{ cursor: "pointer", textDecoration: "none" }}
                    >
                      <span data-text="Apply for this role">
                        Apply for this role
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* about area end */}

          {/* details area start */}
          <div className="details-area">
            <div className="container">
              <div className="details-area-inner section-spacing-bottom">
                <div className="has_fade_anim overflow-hidden">
                  <h1 className="section-title">{position.title}</h1>
                </div>
                <ul className="job-meta has_fade_anim">
                  <li>
                    <span className="title">Location</span>
                    <span className="text">{position.location}</span>
                  </li>
                  <li>
                    <span className="title">Status</span>
                    <span className="text">{position.date}</span>
                  </li>
                  <li>
                    <span className="title">Job Type</span>
                    <span className="text">{position.jobType}</span>
                  </li>
                </ul>
                <div className="job-description has_fade_anim">
                  <div className="text-wrapper">
                    <p className="text">{position.description}</p>
                  </div>
                </div>
                <div className="job-description has_fade_anim">
                  <div className="text-wrapper">
                    <p className="text">{position.details}</p>
                  </div>
                </div>

                {(position.sections || []).map((section: any, index: number) => (
                  <div className="info-content" key={index}>
                    <h2 className="title has_fade_anim">{section.title}</h2>
                    <ul className="info-text has_fade_anim">
                      {(section.items || []).map((item: any, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}

                {position.note && (
                  <div className="ending-note">
                    <div className="text-wrapper">
                      <p className="text has_fade_anim" style={{ fontStyle: "italic", opacity: 0.85 }}>
                        {position.note}
                      </p>
                    </div>
                  </div>
                )}

                {/* Application Section */}
                <div
                  id="apply-section"
                  style={{
                    marginTop: "60px",
                    padding: "44px",
                    borderRadius: "16px",
                    border: "1px solid var(--border, rgba(255,255,255,0.12))",
                    background: "var(--card-bg, rgba(255,255,255,0.02))",
                    backdropFilter: "blur(12px)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.06)"
                  }}
                >
                  <div style={{ marginBottom: "32px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        fontSize: "12px",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        fontWeight: 600,
                        color: "#6366f1",
                        marginBottom: "8px"
                      }}
                    >
                      MindSyncX Careers
                    </span>
                    <h2 style={{ fontSize: "32px", fontWeight: 700, margin: "0 0 10px" }}>
                      Apply for {position.title}
                    </h2>
                    <p style={{ color: "var(--secondary, #8E8E8E)", fontSize: "16px", margin: 0 }}>
                      Complete this quick form or send your resume directly to{" "}
                      <a
                        href={`mailto:careers@mindsyncx.com?subject=Application: ${encodeURIComponent(position.title)}`}
                        style={{ color: "#6366f1", textDecoration: "underline" }}
                      >
                        careers@mindsyncx.com
                      </a>
                    </p>
                  </div>

                  {submitted ? (
                    <div
                      style={{
                        padding: "36px",
                        borderRadius: "12px",
                        background: "rgba(34, 197, 94, 0.08)",
                        border: "1px solid rgba(34, 197, 94, 0.25)",
                        textAlign: "center"
                      }}
                    >
                      <div
                        style={{
                          width: "56px",
                          height: "56px",
                          borderRadius: "50%",
                          background: "#22c55e",
                          color: "#fff",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "16px"
                        }}
                      >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                      <h3 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "8px" }}>
                        Application Received!
                      </h3>
                      <p style={{ color: "var(--secondary, #8E8E8E)", maxWidth: "560px", margin: "0 auto 24px", lineHeight: 1.6 }}>
                        Thank you, <strong style={{ color: "var(--primary, #121212)" }}>{applicantName}</strong>. Our engineering and talent team will review your submission for <strong style={{ color: "var(--primary, #121212)" }}>{position.title}</strong> and reach out via email within 48 business hours.
                      </p>
                      <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                        <a
                          href={mailtoUrl}
                          className="wc-btn wc-btn-normal"
                          style={{
                            padding: "12px 24px",
                            borderRadius: "8px",
                            fontSize: "14px",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          Send Additional Attachments via Email
                        </a>
                        <Link
                          to="/career"
                          style={{
                            padding: "12px 24px",
                            borderRadius: "8px",
                            fontSize: "14px",
                            textDecoration: "none",
                            border: "1px solid var(--border, rgba(0,0,0,0.12))",
                            color: "var(--primary, #121212)",
                            display: "inline-flex",
                            alignItems: "center"
                          }}
                        >
                          Explore Other Roles
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleApplySubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px" }}>
                        <div>
                          <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px", color: "var(--primary, #121212)" }}>
                            Full Name <span style={{ color: "#ef4444" }}>*</span>
                          </label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. Alex Morgan"
                            value={applicantName}
                            onChange={(e) => setApplicantName(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "14px 18px",
                              borderRadius: "8px",
                              border: "1px solid var(--border, rgba(255,255,255,0.15))",
                              background: "rgba(128,128,128,0.06)",
                              color: "var(--primary, #121212)",
                              fontSize: "15px",
                              outline: "none"
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px", color: "var(--primary, #121212)" }}>
                            Email Address <span style={{ color: "#ef4444" }}>*</span>
                          </label>
                          <input
                            type="email"
                            required
                            placeholder="alex@example.com"
                            value={applicantEmail}
                            onChange={(e) => setApplicantEmail(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "14px 18px",
                              borderRadius: "8px",
                              border: "1px solid var(--border, rgba(255,255,255,0.15))",
                              background: "rgba(128,128,128,0.06)",
                              color: "var(--primary, #121212)",
                              fontSize: "15px",
                              outline: "none"
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px" }}>
                        <div>
                          <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px", color: "var(--primary, #121212)" }}>
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            placeholder="+1 (555) 000-0000"
                            value={applicantPhone}
                            onChange={(e) => setApplicantPhone(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "14px 18px",
                              borderRadius: "8px",
                              border: "1px solid var(--border, rgba(255,255,255,0.15))",
                              background: "rgba(128,128,128,0.06)",
                              color: "var(--primary, #121212)",
                              fontSize: "15px",
                              outline: "none"
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px", color: "var(--primary, #121212)" }}>
                            Portfolio / LinkedIn / GitHub URL <span style={{ color: "#ef4444" }}>*</span>
                          </label>
                          <input
                            type="url"
                            required
                            placeholder="https://linkedin.com/in/... or github.com/..."
                            value={applicantPortfolio}
                            onChange={(e) => setApplicantPortfolio(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "14px 18px",
                              borderRadius: "8px",
                              border: "1px solid var(--border, rgba(255,255,255,0.15))",
                              background: "rgba(128,128,128,0.06)",
                              color: "var(--primary, #121212)",
                              fontSize: "15px",
                              outline: "none"
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: "14px", fontWeight: 600, marginBottom: "8px", color: "var(--primary, #121212)" }}>
                          Cover Note & Why You're Interested
                        </label>
                        <textarea
                          rows={4}
                          placeholder="Briefly introduce yourself and what excites you about this role at MindSyncX..."
                          value={applicantNote}
                          onChange={(e) => setApplicantNote(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "14px 18px",
                            borderRadius: "8px",
                            border: "1px solid var(--border, rgba(255,255,255,0.15))",
                            background: "rgba(128,128,128,0.06)",
                            color: "var(--primary, #121212)",
                            fontSize: "15px",
                            outline: "none",
                            resize: "vertical"
                          }}
                        />
                      </div>

                      <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap", marginTop: "12px" }}>
                        <button
                          type="submit"
                          disabled={submitting}
                          className="wc-btn wc-btn-primary"
                          style={{
                            padding: "14px 36px",
                            borderRadius: "8px",
                            fontSize: "15px",
                            fontWeight: 600,
                            cursor: submitting ? "not-allowed" : "pointer",
                            border: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          {submitting ? "Submitting..." : "Submit Application"}
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </button>
                        <a
                          href={mailtoUrl}
                          style={{
                            color: "var(--secondary, #8E8E8E)",
                            fontSize: "14px",
                            textDecoration: "underline"
                          }}
                        >
                          Or apply directly via email
                        </a>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* details area end */}
        </section>
      </main>
    </>
  );
};

export default CareerDetails;
